import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { axiosInstance, getAiRequestErrorMessage } from "@/lib/api";
import { POLL_IDLE_MS, clampWhenHidden } from "@/lib/realtime";
import { queue } from "@/components/Toast";
import { useAuthStore } from "@/stores/authStore";
import type {
  WorkshopArtifactNote,
  WorkshopArtifactNoteStatus,
} from "@/types/workshop.type";
import {
  workshopArtifactNotesKey,
  workshopArtifactNotesRootKey,
  workshopArtifactsKey,
} from "./workshopKeys";

/** Every note on one artifact, oldest first. */
export function useWorkshopArtifactNotes(projectId: string | null, artifactId: string | null) {
  return useQuery({
    queryKey: workshopArtifactNotesKey(projectId, artifactId),
    queryFn: async () => {
      const res = await axiosInstance.get<{ notes: WorkshopArtifactNote[] }>(
        `/workshop/artifacts/${artifactId}/notes`,
      );
      return res.data.notes;
    },
    enabled: !!projectId && !!artifactId,
    // Hints from the project room refresh it; this is only the safety net.
    refetchInterval: () => clampWhenHidden(POLL_IDLE_MS),
  });
}

type NotesCache = WorkshopArtifactNote[] | undefined;

/** One note's slot in the cache: replaced, removed (null) or put back. */
function writeNote(
  qc: QueryClient,
  key: ReturnType<typeof workshopArtifactNotesKey>,
  id: string,
  next: WorkshopArtifactNote | null,
) {
  qc.setQueryData<NotesCache>(key, (list) => {
    if (!list) return next ? [next] : list;
    const at = list.findIndex((n) => n.id === id);
    if (at < 0) {
      if (!next) return list;
      // Back into reading order (oldest first).
      return [...list, next].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    }
    if (!next) return list.filter((n) => n.id !== id);
    return list.map((n) => (n.id === id ? next : n));
  });
}

const failed = (title: string) => (error: unknown) =>
  queue.add({ title, description: getAiRequestErrorMessage(error), variant: "error" });

/**
 * Add, rewrite, resolve / reopen and delete notes. Every one is optimistic and
 * rolls back only the note it touched.
 */
export function useWorkshopArtifactNoteMutations(projectId: string, artifactId: string) {
  const qc = useQueryClient();
  const viewerId = useAuthStore((state) => state.user?.id ?? "");
  const key = workshopArtifactNotesKey(projectId, artifactId);
  const find = (id: string) => (qc.getQueryData<NotesCache>(key) ?? []).find((n) => n.id === id);

  const settle = () => {
    void qc.invalidateQueries({ queryKey: workshopArtifactNotesRootKey(projectId) });
    // The open-note count on the artifact's row.
    void qc.invalidateQueries({ queryKey: workshopArtifactsKey(projectId) });
  };

  const createNote = useMutation({
    mutationFn: async (vars: {
      tempId: string;
      quote: string;
      prefix: string;
      suffix: string;
      body: string;
      artifactVersion: number;
    }) => {
      const res = await axiosInstance.post<{ note: WorkshopArtifactNote }>(
        `/workshop/artifacts/${artifactId}/notes`,
        {
          quote: vars.quote,
          prefix: vars.prefix,
          suffix: vars.suffix,
          body: vars.body,
          artifactVersion: vars.artifactVersion,
        },
      );
      return res.data.note;
    },
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: key });
      const now = new Date().toISOString();
      writeNote(qc, key, vars.tempId, {
        id: vars.tempId,
        artifactId,
        projectId,
        quote: vars.quote,
        prefix: vars.prefix,
        suffix: vars.suffix,
        artifactVersion: vars.artifactVersion,
        body: vars.body,
        status: "open",
        createdByUserId: viewerId,
        resolvedAt: null,
        resolution: null,
        createdAt: now,
        updatedAt: now,
      });
    },
    onSuccess: (note, vars) => {
      writeNote(qc, key, vars.tempId, null);
      writeNote(qc, key, note.id, note);
    },
    onError: (error, vars) => {
      writeNote(qc, key, vars.tempId, null);
      failed("Couldn't add the note")(error);
    },
    onSettled: settle,
  });

  const updateNote = useMutation({
    mutationFn: async (vars: {
      noteId: string;
      body?: string;
      status?: WorkshopArtifactNoteStatus;
    }) => {
      const { noteId, ...body } = vars;
      const res = await axiosInstance.patch<{ note: WorkshopArtifactNote }>(
        `/workshop/notes/${noteId}`,
        body,
      );
      return res.data.note;
    },
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: key });
      const before = find(vars.noteId);
      if (before) {
        const resolving = vars.status === "resolved" && before.status !== "resolved";
        const reopening = vars.status === "open" && before.status !== "open";
        writeNote(qc, key, vars.noteId, {
          ...before,
          ...(vars.body !== undefined ? { body: vars.body } : {}),
          ...(vars.status ? { status: vars.status } : {}),
          ...(resolving ? { resolvedAt: new Date().toISOString() } : {}),
          ...(reopening ? { resolvedAt: null, resolution: null } : {}),
        });
      }
      return { before };
    },
    onSuccess: (note) => writeNote(qc, key, note.id, note),
    onError: (error, vars, context) => {
      if (context?.before) writeNote(qc, key, vars.noteId, context.before);
      failed("Couldn't update the note")(error);
    },
    onSettled: settle,
  });

  const deleteNote = useMutation({
    mutationFn: async (noteId: string) => {
      await axiosInstance.delete(`/workshop/notes/${noteId}`);
    },
    onMutate: async (noteId) => {
      await qc.cancelQueries({ queryKey: key });
      const before = find(noteId);
      writeNote(qc, key, noteId, null);
      return { before };
    },
    onError: (error, noteId, context) => {
      if (context?.before) writeNote(qc, key, noteId, context.before);
      failed("Couldn't delete the note")(error);
    },
    onSettled: settle,
  });

  return { createNote, updateNote, deleteNote };
}
